import * as CONSTANTS from './constants'

import { TimeoutError, getParsedActionEndpoint, timeoutFetch } from './utils.js'
import {
  USER_LOGOUT,
  clearLoggedInfo,
  getLoggedUser,
  refreshToken,
} from '../auth/actions.js'

import has from 'lodash/has'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import camelize from 'camelcase-keys'
import { saveAs } from 'file-saver'
import { recordEvent } from '../../observability/events'

const { LOADING, RESULT, ERROR, LAST_SUCCESSFUL_RESULT } = CONSTANTS.STATE_TYPES
const DEFAULT_RETRIES = 1

const BASE_ENDPOINT = process.env.REACT_APP_BASE_URL_GATEWAY

export const api = (service, route) => {
  const serviceGroup = CONSTANTS.SERVICES_GROUPS_MAP[service]
  const basePath = serviceGroup
    ? `/${serviceGroup}/api/${route}`
    : `/api/${route}`
  return `${BASE_ENDPOINT}${basePath}`
}

export class AuthenticationError extends Error {}
export class ServerError extends Error {}

export const SERVER_ERROR = (error) => ({
  type: CONSTANTS.SERVER_ERROR,
  error,
})

export const NETWORK_ERROR = (error) => ({
  type: CONSTANTS.NETWORK_ERROR,
  error,
})

export const HIDE_ERROR = (id) => ({
  type: CONSTANTS.HIDE_ERROR,
  id,
})

export const HIDE_ALL_ERRORS = () => ({
  type: CONSTANTS.HIDE_ALL_ERRORS,
})

export const setAppError = (errorMessage) => {
  const error = {}
  error.message = errorMessage
  return {
    type: CONSTANTS.APP_ERROR,
    error,
  }
}

const parseJSONResponse = async (response) => {
  let data = {}
  const text = await response.text()
  if (!isEmpty(text)) {
    try {
      data = JSON.parse(text)
    } catch (e) {
      // JSON parse failed — return empty object
    }
  }

  return camelize(data)
}

// Um mesmo endpoint falhando em sequência (retry, re-render) vira um evento só
// nesta janela; o throttle é por método + endpoint + status.
const API_FAILURE_THROTTLE_MS = 30 * 1000
const lastApiFailureAt = new Map()

// Registra a falha de uma chamada na observabilidade. Só método, serviço,
// endpoint (sem query string) e status: nunca token, headers ou corpo.
// Nunca rejeita (recordEvent engole os próprios erros).
export const recordApiFailure = ({
  method,
  service,
  url,
  status,
  timeout,
  forcedLogout,
}) => {
  const endpoint = String(url).split('?')[0]
  const key = `${method} ${endpoint} ${status ?? 'network'}`
  const now = Date.now()
  if (now - (lastApiFailureAt.get(key) ?? 0) < API_FAILURE_THROTTLE_MS) {
    return Promise.resolve()
  }
  lastApiFailureAt.set(key, now)

  if (status === undefined) {
    return recordEvent({
      level: 'warn',
      category: 'outro',
      action: 'api_network_error',
      payload: { method, service, endpoint, timeout },
    })
  }

  const isAuth = status === 401 || status === 403
  return recordEvent({
    level: isAuth || status >= 500 ? 'error' : 'warn',
    category: isAuth ? 'auth' : 'outro',
    action: 'api_http_error',
    payload: { method, service, endpoint, status, forcedLogout },
  })
}

const performRequest = async (
  dispatch,
  method,
  service,
  url,
  body,
  shouldRefreshToken = true,
) => {
  dispatch({
    type: CONSTANTS.API_CLEAR,
    url,
  })

  dispatch({
    type: CONSTANTS.API_LOADING,
    url,
  })

  const authentication = await getLoggedUser()
  let params = {
    method,
    headers: {
      Authorization: `Bearer ${authentication.token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    cache: 'no-store',
  }

  if (!isEmpty(body)) {
    params.body = JSON.stringify(body)
  }

  return timeoutFetch(api(service, url), params)
    .catch((error) => {
      // Offline é o esperado no campo: só registra a falha de rede com conexão.
      if (navigator.onLine) {
        recordApiFailure({
          method,
          service,
          url,
          timeout: error instanceof TimeoutError,
        })
      }

      if (error instanceof TimeoutError) {
        dispatch(NETWORK_ERROR(error))
      } else {
        dispatch(SERVER_ERROR(error))
      }

      dispatch({
        type: CONSTANTS.API_ERROR,
        url,
        error,
      })
    })
    .then(async (response) => {
      if (!response) return null

      // Não fazer refresh do token para rotas de registro/primeiro acesso
      const noRefreshRoutes = ['CompleteGigyaRegistration', 'temporary-login']
      const shouldSkipRefresh = noRefreshRoutes.some((route) =>
        url.includes(route),
      )

      if (response.status === 401 && shouldRefreshToken && !shouldSkipRefresh) {
        const refreshed = await refreshToken()
        if (refreshed.refreshed) {
          return performRequest(dispatch, method, service, url, body, false)
        }
      }

      // Depois do retry com token renovado: um 401 recuperado não chega aqui.
      // Espera o registro: o logout abaixo apaga o login de onde o evento lê o
      // userId. recordEvent nunca lança.
      if (response.status >= 400) {
        await recordApiFailure({
          method,
          service,
          url,
          status: response.status,
          forcedLogout: response.status >= 401 && !shouldSkipRefresh,
        })
      }

      // Não fazer logout automático para rotas de registro/primeiro acesso
      if (response.status >= 401 && !shouldSkipRefresh) {
        await clearLoggedInfo()
        dispatch(USER_LOGOUT('Your session token has expired'))
        Promise.reject('Your session token has expired')
      }

      let result = await parseJSONResponse(response)

      if (isArray(result.responseList)) {
        result = result.responseList
      } else {
        result = {
          ...(!isEmpty(result.response) ? result.response : {}),
          token: result.token,
          errors: camelize(result.errors || {}),
          status: result.status,
          title: result.title || result.mensagem,
          hasErrors:
            !isEmpty(result.errors) ||
            (has(result, 'sucesso') && !result.sucesso),
        }
      }

      if (result.hasErrors) {
        dispatch({
          type: CONSTANTS.API_ERROR,
          url,
          result,
        })
      } else {
        dispatch({
          type: CONSTANTS.API_SUCCESS,
          url,
          result,
        })
      }

      return result
    })
}

export async function post(dispatch, service, url, body) {
  return performRequest(dispatch, 'post', service, url, body)
}

export async function del(dispatch, service, url, body) {
  return performRequest(dispatch, 'delete', service, url, body)
}

export async function put(dispatch, service, url, body) {
  return performRequest(dispatch, 'put', service, url, body)
}

export async function get(dispatch, service, url) {
  return performRequest(dispatch, 'get', service, url)
}

export function getApiState(state, endpoint, type) {
  return state.api[`${getParsedActionEndpoint(endpoint)}.${type}`]
}

export function getApiLoading(state, endpoint) {
  return getApiState(state, endpoint, LOADING)
}

export function getApiError(state, endpoint) {
  return getApiState(state, endpoint, ERROR)
}

export function getApiResult(state, endpoint) {
  return getApiState(state, endpoint, RESULT)
}

export function getApiLastSuccessfulResult(state, endpoint) {
  return getApiState(state, endpoint, LAST_SUCCESSFUL_RESULT)
}
